import type { NodeHandle } from '@/models/logic/RuntimeHandles';
import { LogicGraph } from './LogicGraph';
import { TileInstance } from '@/models/tiles/TileInstance';
import { Side, TileSideIOType } from '@/schemas/tiles';
import { TileDefinition, GraphBuildQueueItem } from '@/models/tiles/TileDefinition';
import { ITileManagerModel, IPoint, IConnectionModel } from '@/interfaces/models';
import {
  Direction,
  opposite,
  directionRotateCW,
  pointToDirection,
  directionToSide,
} from '@/utils/direction';
import { Rotation } from '@/models/data/Rotation';
import { Point } from '@/models/Point';
import { TileType } from '@/models/TileTypes';
import { BridgeTileState } from '@/models/tiles/BridgeTile';
import { distinct } from '@/utils/object';
import { oppositeSide } from '@/schemas/common';

export function getNeighborPosition(pos: IPoint, dir: Direction): Point {
  switch (dir) {
    case Direction.UP:
      return new Point(pos.x, pos.y - 1);
    case Direction.DOWN:
      return new Point(pos.x, pos.y + 1);
    case Direction.LEFT:
      return new Point(pos.x - 1, pos.y);
    case Direction.RIGHT:
      return new Point(pos.x + 1, pos.y);
  }
}

function localSideToDirection(side: Side): Direction | null {
  switch (side) {
    case Side.TOP:
      return Direction.UP;
    case Side.RIGHT:
      return Direction.RIGHT;
    case Side.BOTTOM:
      return Direction.DOWN;
    case Side.LEFT:
      return Direction.LEFT;
    default:
      return null;
  }
}

export function getGlobalDirection(side: Side, rotation: Rotation): Direction | null {
  let dir = localSideToDirection(side);
  if (dir === null) return null;

  for (let i = 0; i < rotation; i++) {
    dir = directionRotateCW(dir);
  }
  return dir;
}

export function getLocalSide(globalDir: Direction, rotation: Rotation): Side | null {
  let dir = globalDir;
  const rotations = (4 - rotation) % 4;
  for (let i = 0; i < rotations; i++) {
    dir = directionRotateCW(dir);
  }

  switch (dir) {
    case Direction.UP:
      return Side.TOP;
    case Direction.RIGHT:
      return Side.RIGHT;
    case Direction.DOWN:
      return Side.BOTTOM;
    case Direction.LEFT:
      return Side.LEFT;
    default:
      return null;
  }
}

export function resolveConnectionSides(
  tileA: Pick<TileInstance, 'location'> & {
    meta: Pick<TileInstance['meta'], 'rotation'>;
  },
  tileB: Pick<TileInstance, 'location'> & {
    meta: Pick<TileInstance['meta'], 'rotation'>;
  },
): { sideA: Side; sideB: Side } | null {
  const dirAtoB = pointToDirection(
    Point.fromData(tileB.location).getNeighborDirection(Point.fromData(tileA.location)),
  );
  const dirBtoA = pointToDirection(
    Point.fromData(tileA.location).getNeighborDirection(Point.fromData(tileB.location)),
  );

  const sideA = getLocalSide(dirAtoB, (tileA.meta.rotation as Rotation) ?? Rotation.CW_0);
  const sideB = getLocalSide(dirBtoA, (tileB.meta.rotation as Rotation) ?? Rotation.CW_0);

  if (!sideA || !sideB) {
    return null;
  }

  return { sideA, sideB };
}

export function setAllSidesNodeId(instance: TileInstance, nodeId: NodeHandle | undefined) {
  instance.logicNodeIds = {
    [Side.TOP]: nodeId,
    [Side.RIGHT]: nodeId,
    [Side.BOTTOM]: nodeId,
    [Side.LEFT]: nodeId,
  };
}

export function standardBuildAndConnect(
  nodeType: string,
  data: {
    graph: LogicGraph;
    tileManager: ITileManagerModel;
    instance: TileInstance;
    sourceNodeId?: NodeHandle;
    sourceTileId?: string;
    entryDirection?: Direction;
  },
  definition: TileDefinition<any>,
): GraphBuildQueueItem[] {
  const { graph, tileManager, instance, sourceNodeId, sourceTileId, entryDirection } =
    data;

  if (
    instance.logicNodeIds &&
    distinct(Object.values(instance.logicNodeIds)).length > 1
  ) {
    throw new Error(
      "standardBuildAndConnect doesn't support tiles with multiple logic nodes",
    );
  }

  // 1. Create or get node
  let nodeId = instance.logicNodeIds?.[Side.TOP];
  if (!nodeId) {
    const node = graph.addNode(nodeType);
    nodeId = node.id;
    setAllSidesNodeId(instance, nodeId);
  }

  // 2. Connect input if source provided
  if (sourceNodeId && entryDirection !== undefined) {
    const incomingGlobalDir = opposite(entryDirection);
    const localSide = getLocalSide(incomingGlobalDir, instance.meta.rotation as Rotation);

    if (localSide) {
      const io = definition.ioSides[localSide];
      if (io.type === TileSideIOType.INPUT || io.type === TileSideIOType.INPUT_OUTPUT) {
        graph.addEdge(sourceNodeId, nodeId, {
          sourceTileId,
          targetTileId: instance.id,
          targetSide: localSide,
        });
      }
    }
  }

  // 3. Find neighbors by position adjacency
  const queueItems: GraphBuildQueueItem[] = [];

  const sides = [Side.TOP, Side.RIGHT, Side.BOTTOM, Side.LEFT];

  for (const side of sides) {
    const io = definition.ioSides[side];
    if (io.type === TileSideIOType.OUTPUT || io.type === TileSideIOType.INPUT_OUTPUT) {
      const globalDir = getGlobalDirection(side, instance.meta.rotation as Rotation);
      if (globalDir !== null) {
        const neighborPos = getNeighborPosition(
          Point.fromData(instance.location),
          globalDir,
        );
        const neighbor = tileManager.get(neighborPos);

        if (neighbor) {
          queueItems.push({
            tile: neighbor,
            sourceNodeId: nodeId,
            sourceTileId: instance.id,
            entryDirection: globalDir,
          });
        }
      }
    }
  }

  return queueItems;
}

export function wireBuildAndConnect(data: {
  graph: LogicGraph;
  tileManager: ITileManagerModel;
  connectionModel?: IConnectionModel;
  instance: TileInstance;
  sourceNodeId?: NodeHandle;
  sourceTileId?: string;
  entryDirection?: Direction;
}): GraphBuildQueueItem[] {
  const {
    graph,
    tileManager,
    connectionModel,
    instance,
    sourceNodeId,
    sourceTileId,
    entryDirection,
  } = data;

  if (
    instance.logicNodeIds &&
    distinct(Object.values(instance.logicNodeIds)).length > 1
  ) {
    throw new Error(
      "wireBuildAndConnect doesn't support tiles with multiple logic nodes",
    );
  }

  // 1. If wire already has a node, use it.
  let nodeId = instance.logicNodeIds?.[Side.TOP];

  if (!nodeId) {
    // Create a new node (WIRE type for OR logic)
    const node = graph.addNode('WIRE');
    nodeId = node.id;

    // Traverse all connected wires and assign this node ID
    const visitedWires = new Map<string, TileInstance>();
    const wireQueue: TileInstance[] = [instance];
    let wireQueueHead = 0;
    visitedWires.set(instance.id, instance);

    while (wireQueueHead < wireQueue.length) {
      const wire = wireQueue[wireQueueHead++];
      setAllSidesNodeId(wire, nodeId);

      const sides = [Side.TOP, Side.RIGHT, Side.BOTTOM, Side.LEFT];
      for (const side of sides) {
        // Check if this wire connects to this side
        let isConnected = false;
        if (connectionModel) {
          isConnected = connectionModel.connectionFlags(wire.id)[side];
        } else {
          isConnected = wire.meta.connections?.[side] ?? true;
        }
        if (!isConnected) continue;

        const globalDir = getGlobalDirection(side, wire.meta.rotation as Rotation);
        if (globalDir !== null) {
          const neighborPos = getNeighborPosition(
            Point.fromData(wire.location),
            globalDir,
          );
          const neighbor = tileManager.get(neighborPos);

          if (neighbor && !visitedWires.has(neighbor.id)) {
            // Check if neighbor connects back to us
            const neighborIncomingSide = getLocalSide(
              opposite(globalDir),
              neighbor.meta.rotation as Rotation,
            );
            if (!neighborIncomingSide) continue;

            // Don't cross chip boundaries in wire BFS
            if (wire.meta.partOfChip !== neighbor.meta.partOfChip) continue;

            if (neighbor.tileType === TileType.Wire && !neighbor.meta.bundle) {
              let neighborConnected = false;
              if (connectionModel) {
                neighborConnected = connectionModel.connectionFlags(neighbor.id)[
                  neighborIncomingSide
                ];
              } else {
                neighborConnected =
                  neighbor.meta.connections?.[neighborIncomingSide] ?? true;
              }

              if (neighborConnected) {
                if (
                  neighbor.logicNodeIds?.[Side.TOP] &&
                  neighbor.logicNodeIds[Side.TOP] !== nodeId
                ) {
                  // Keep both nets alive while the build queue still refers to
                  // them. GraphManager collapses WIRE edges after traversal and
                  // rewrites every tile reference together with their edges.
                  graph.addEdge(nodeId, neighbor.logicNodeIds[Side.TOP]);
                } else if (!neighbor.logicNodeIds?.[Side.TOP]) {
                  visitedWires.set(neighbor.id, neighbor);
                  wireQueue.push(neighbor);
                }
                // Already in our net — skip
              }
            } else if (neighbor.tileType === TileType.Bridge) {
              // Traverse bridge(s) to find connected wires
              let current: TileInstance | undefined = neighbor;
              let dir = globalDir;

              // Bridge passes straight through
              while (current && current.tileType === TileType.Bridge) {
                const nextPos = getNeighborPosition(
                  Point.fromData(current.location),
                  dir,
                );
                current = tileManager.get(nextPos);
              }

              if (
                current &&
                current.tileType === TileType.Wire &&
                // An assigned net is connected by the queued bridge traversal.
                // Reassigning it here would leave its existing edges behind.
                !current.logicNodeIds?.[Side.TOP] &&
                !visitedWires.has(current.id)
              ) {
                visitedWires.set(current.id, current);
                wireQueue.push(current);
              }
            }
          }
        }
      }
    }
  }

  // 2. Connect input if source provided
  if (sourceNodeId && entryDirection !== undefined) {
    const incomingGlobalDir = opposite(entryDirection);
    const localSide = getLocalSide(incomingGlobalDir, instance.meta.rotation as Rotation);

    if (localSide) {
      let isConnected = false;
      if (connectionModel) {
        isConnected = connectionModel.connectionFlags(instance.id)[localSide];
      } else {
        isConnected = instance.meta.connections?.[localSide] ?? true;
      }

      if (isConnected && sourceNodeId !== nodeId) {
        graph.addEdge(sourceNodeId, nodeId, {
          sourceTileId,
          targetTileId: instance.id,
          targetSide: localSide,
        });
      }
    }
  }

  // 3. Find non-wire neighbors to propagate to
  const queueItems: GraphBuildQueueItem[] = [];

  const sides = [Side.TOP, Side.RIGHT, Side.BOTTOM, Side.LEFT];
  for (const side of sides) {
    // Check connection
    let isConnected = false;
    if (connectionModel) {
      isConnected = connectionModel.connectionFlags(instance.id)[side];
    } else {
      isConnected = instance.meta.connections?.[side] ?? true;
    }
    if (!isConnected) continue;

    const globalDir = getGlobalDirection(side, instance.meta.rotation as Rotation);
    if (globalDir !== null) {
      const neighborPos = getNeighborPosition(
        Point.fromData(instance.location),
        globalDir,
      );
      const neighbor = tileManager.get(neighborPos);

      if (neighbor) {
        const isCrossBoundary = instance.meta.partOfChip !== neighbor.meta.partOfChip;
        const isWire = neighbor.tileType === TileType.Wire;

        // Same-boundary wires are handled by step 1's BFS
        if (!isWire || isCrossBoundary) {
          queueItems.push({
            tile: neighbor,
            sourceNodeId: nodeId,
            sourceTileId: instance.id,
            entryDirection: globalDir,
          });
        }
      }
    }
  }

  return queueItems;
}

export function multiInterfaceBuildAndConnect(data: {
  graph: LogicGraph;
  tileManager: ITileManagerModel;
  connectionModel?: IConnectionModel;
  instance: TileInstance;
  sourceNodeId?: NodeHandle;
  sourceTileId?: string;
  entryDirection?: Direction;
}): GraphBuildQueueItem[] {
  const {
    graph,
    tileManager,
    connectionModel,
    instance,
    sourceNodeId,
    sourceTileId,
    entryDirection,
  } = data;

  // Helper: check if a side is active (not NONE via effectiveIoSides)
  const isSideActive = (side: Side): boolean => {
    const io = instance.effectiveIoSides?.[side];
    return !io || io.type !== TileSideIOType.NONE;
  };

  // 1. Create independent WIRE nodes for active sides if not already created
  // An inactive top pin legitimately has no node. Repeated visits must reuse
  // the existing side nodes or earlier connections are left on abandoned nets.
  if (!instance.logicNodeIds) {
    instance.logicNodeIds = {} as Record<Side, NodeHandle>;
    for (const side of [Side.TOP, Side.RIGHT, Side.BOTTOM, Side.LEFT]) {
      if (!isSideActive(side)) continue;
      const node = graph.addNode('WIRE');
      instance.logicNodeIds[side] = node.id;
    }
  }

  // 2. Connect source to entry side's node (only if side is active)
  if (sourceNodeId && entryDirection !== undefined) {
    const incomingGlobalDir = opposite(entryDirection);
    const localSide = getLocalSide(incomingGlobalDir, instance.meta.rotation as Rotation);

    if (localSide && isSideActive(localSide)) {
      const sideNodeId = instance.logicNodeIds[localSide];
      if (sideNodeId && sourceNodeId !== sideNodeId) {
        graph.addEdge(sourceNodeId, sideNodeId, {
          sourceTileId,
          targetTileId: instance.id,
          targetSide: localSide,
        });
      }
    }
  }

  // 3. Queue neighbors on all connected active sides (each with its own node ID)
  const queueItems: GraphBuildQueueItem[] = [];
  for (const side of [Side.TOP, Side.RIGHT, Side.BOTTOM, Side.LEFT]) {
    if (!isSideActive(side)) continue;

    let isConnected = false;
    if (connectionModel) {
      isConnected = connectionModel.connectionFlags(instance.id)[side];
    } else {
      isConnected = instance.meta.connections?.[side] ?? true;
    }
    if (!isConnected) continue;

    const globalDir = getGlobalDirection(side, instance.meta.rotation as Rotation);
    if (globalDir !== null) {
      const neighborPos = getNeighborPosition(
        Point.fromData(instance.location),
        globalDir,
      );
      const neighbor = tileManager.get(neighborPos);

      if (neighbor) {
        queueItems.push({
          tile: neighbor,
          sourceNodeId: instance.logicNodeIds[side]!,
          sourceTileId: instance.id,
          entryDirection: globalDir,
        });
      }
    }
  }

  return queueItems;
}

export function bridgeBuildAndConnect(data: {
  graph: LogicGraph;
  tileManager: ITileManagerModel;
  connectionModel?: IConnectionModel;
  instance: TileInstance;
  sourceNodeId?: NodeHandle;
  sourceTileId?: string;
  entryDirection?: Direction;
}): GraphBuildQueueItem[] {
  const { tileManager, instance, sourceNodeId, sourceTileId, entryDirection } = data;

  if (!sourceNodeId || entryDirection === undefined) return [];

  // Update bridge state with node IDs for visualization
  if (!instance.state) instance.state = {};
  if (!instance.logicNodeIds) instance.logicNodeIds = {};
  if (entryDirection === Direction.UP || entryDirection === Direction.DOWN) {
    (instance.state as BridgeTileState).verticalNodeId = sourceNodeId;
    instance.logicNodeIds[Side.TOP] = sourceNodeId;
    instance.logicNodeIds[Side.BOTTOM] = sourceNodeId;
  } else {
    (instance.state as BridgeTileState).horizontalNodeId = sourceNodeId;
    instance.logicNodeIds[Side.LEFT] = sourceNodeId;
    instance.logicNodeIds[Side.RIGHT] = sourceNodeId;
  }

  const queueItems: GraphBuildQueueItem[] = [];

  // Bridge passes signal straight through:
  // Moving RIGHT -> Continue RIGHT
  // Moving LEFT -> Continue LEFT
  // Moving UP -> Continue UP
  // Moving DOWN -> Continue DOWN
  const exitDirection = entryDirection;

  const neighborPos = getNeighborPosition(
    Point.fromData(instance.globalTilePosition.location),
    exitDirection,
  );
  const neighbor = tileManager.get(neighborPos);

  const neighborSide = oppositeSide(directionToSide(exitDirection));

  if (neighbor) {
    // Prevent self-loops if neighbor is already part of the same net
    if (sourceNodeId && neighbor.logicNodeIds?.[neighborSide] === sourceNodeId) {
      return [];
    }

    queueItems.push({
      tile: neighbor,
      sourceNodeId,
      sourceTileId,
      entryDirection: exitDirection,
    });
  }

  return queueItems;
}
